import { TitleCasePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { loginRoutes } from '@capturum/login';
import { BasicLayoutComponent } from '@core/components/basic-layout/basic-layout.component';
import { LandingComponent } from '@core/components/landing/landing.component';
import { AppRoutes } from '@core/enums/routes.enum';
import { authGuard } from '@core/guards/auth.guard';
import { canDeactivateGuard } from '@core/guards/can-deactivate.guard';
import { permissionsGuard } from '@core/guards/permissions.guard';
import { provideStates } from '@ngxs/store';
import { pageConfigResolver } from '@shared/resolvers/page-config.resolver';
import { translationResolver } from '@shared/resolvers/translation.resolver';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { FlowUIState } from './state/flow-ui/flow-ui.state';
import { UserState } from './state/user/user.state';

const loginLayoutComponent = async () => {
  const { LoginLayoutComponent } = await import('@core/components/login-layout/login-layout.component');

  return LoginLayoutComponent;
};

export const appRoutes: Routes = [
  {
    path: 'auth',
    loadComponent: loginLayoutComponent,
    providers: [provideStates([UserState])],
    resolve: { translation: translationResolver },
    children: [
      ...loginRoutes[0].children.map((route) => {
        return {
          ...route,
          loadComponent: async () => {
            if (route.path === AppRoutes.login) {
              const { LoginComponent } = await import('@features/login/components/login/login.component');

              return LoginComponent;
            }

            if (route.path === 'forgot-password') {
              const { ForgotPasswordComponent } =
                await import('@features/auth/pages/forgot-password/forgot-password.component');

              return ForgotPasswordComponent;
            }

            return route.loadComponent ? route.loadComponent() : undefined;
          },
          ...(route.path === AppRoutes.login && { canDeactivate: [canDeactivateGuard] }),
        };
      }),
      {
        path: AppRoutes.acceptInvite,
        children: [
          {
            path: ':token',
            loadComponent: () => {
              return import('./features/auth/pages/accept-invite/accept-invite.component').then((m) => {
                return m.AcceptInviteComponent;
              });
            },
          },
        ],
      },
    ],
  },
  {
    path: `tenant/${AppRoutes.register}`,
    resolve: { translation: translationResolver },
    loadComponent: loginLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () => {
          return import('./features/auth/pages/register/register.component').then((m) => {
            return m.RegisterComponent;
          });
        },
      },
    ],
  },
  {
    path: `tenant/${AppRoutes.confirmRegistration}`,
    resolve: { translation: translationResolver },
    loadComponent: loginLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () => {
          return import('./features/tenant/pages/confirm-registration/confirm-registration.component').then((m) => {
            return m.ConfirmRegistrationComponent;
          });
        },
      },
    ],
  },
  {
    path: '',
    providers: [provideStates([FlowUIState, UserState])],
    canActivate: [authGuard],
    resolve: {
      translation: translationResolver,
    },
    children: [
      {
        path: '',
        component: BasicLayoutComponent,
        canActivate: [permissionsGuard],
        children: [
          {
            path: '',
            component: LandingComponent,
            pathMatch: 'full',
          },
          {
            path: AppRoutes.user,
            resolve: { data: pageConfigResolver },
            children: [
              {
                path: AppRoutes.profile,
                resolve: { data: pageConfigResolver },
                loadComponent: async () => {
                  const { UserProfileComponent } =
                    await import('./features/user/pages/user-profile/user-profile.component');

                  return UserProfileComponent;
                },
                data: {
                  pageConfig: {
                    title: 'menu-item.profile',
                    backUrl: AppRoutes.application,
                  },
                },
              },
            ],
          },
          {
            path: AppRoutes.token,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            providers: [TitleCasePipe],
            loadChildren: async () => {
              const routes = await import('./features/token-management/routes');

              return routes.routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.token',
              },
              permissions: {
                only: ['token.show', 'token.manage'],
              },
            },
          },
          {
            path: AppRoutes.oAuthClient,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/oauth-client/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.oauth-clients',
              },
              permissions: {
                only: ['oauth_client.show', 'oauth_client.manage'],
              },
            },
          },
          {
            path: AppRoutes.permission,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadComponent: () => {
              return import('./features/permission-management/permission-management.component').then((m) => {
                return m.PermissionManagementComponent;
              });
            },
            data: {
              pageConfig: {
                title: 'menu-item.permission',
              },
              permissions: {
                only: ['permission.show'],
              },
            },
          },
          {
            path: AppRoutes.flowCreator,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const routes = await import('./features/flow-ui-creator/routes');

              return routes.routes;
            },
            data: {
              permissions: {
                only: ['flow.show', 'flow.manage'],
              },
            },
          },
          {
            path: AppRoutes.application,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            providers: [TitleCasePipe],
            loadChildren: async () => {
              const routes = await import('./features/applications/routes');

              return routes.routes;
            },
            data: {
              permissions: {
                only: ['application.show', 'application.manage'],
              },
            },
          },
          {
            path: AppRoutes.apps,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const routes = await import('./features/apps/routes');

              return routes.routes;
            },
            data: {
              permissions: {
                only: ['paas_app.show'],
              },
            },
          },
          {
            path: AppRoutes.statistic,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const routes = await import('./features/statistics/routes');

              return routes.routes;
            },
            data: {
              permissions: {
                only: ['flow_metric.show', 'node_metric.show'],
              },
            },
          },
          {
            path: AppRoutes.uptimeMonitoring,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/uptime-monitoring/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'uptime.overview.title',
              },
              permissions: {
                only: ['application_uptime.show'],
              },
            },
          },
          {
            path: AppRoutes.secret,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            providers: [TitleCasePipe],
            loadChildren: async () => {
              const routes = await import('./features/secret-management/routes');

              return routes.routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.secret',
              },
              permissions: {
                only: ['secret.show', 'secret.manage'],
              },
            },
          },
          {
            path: AppRoutes.proxies,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/proxy/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.proxy',
              },
              permissions: {
                only: ['http_proxy.show', 'http_proxy.manage'],
              },
            },
          },
          {
            path: AppRoutes.globals,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/global/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.globals',
              },
              permissions: {
                only: ['global.show', 'global.manage'],
              },
            },
          },
          {
            path: AppRoutes.waf,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/waf/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'menu-item.waf',
              },
              permissions: {
                only: ['waf.show', 'waf.manage'],
              },
            },
          },
          {
            path: AppRoutes.chat,
            canActivate: [ngxPermissionsGuard],
            resolve: { data: pageConfigResolver },
            loadComponent: async () => {
              const { ChatPageComponent } = await import('./features/llm/pages/chat/chat.component');

              return ChatPageComponent;
            },
            data: {
              pageConfig: {
                title: 'menu-item.chat',
              },
              permissions: {
                only: ['llm.show'],
              },
            },
          },
          {
            // No permission gate: the in-app docs are available to every authenticated user.
            path: AppRoutes.documentation,
            resolve: { data: pageConfigResolver },
            loadChildren: async () => {
              const { routes } = await import('./features/documentation/routes');

              return routes;
            },
            data: {
              pageConfig: {
                title: 'documentation.nav.title',
              },
            },
          },
          {
            path: 'config',
            loadChildren: async () => {
              const routes = await import('./features/config/routes');

              return routes.routes;
            },
          },
        ],
      },
    ],
  },
  {
    path: 'flow-api-docs/:tenantRef',
    loadComponent: () => {
      return import('./features/flow-api-docs-public/flow-api-docs-public.component').then((m) => {
        return m.FlowApiDocsPublicComponent;
      });
    },
  },
  {
    path: '**',
    redirectTo: '/',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes, { enableViewTransitions: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
